import { Map, OrderedSet } from 'immutable';

import { FILTER_KEYS, DEVICE_GROUP_ITEMS } from './constants';
import { deduplicateItems, parseTeams, parseUsers, updateSelectedItems } from './utils';

export const createFiltersReducer = (types, filtersMap, mapFiltersToState) => {
  const initialState = Map({
    selectedDateFilter: Map({
      type: 'relative',
      value: 7 * 24 * 60 * 60,
    }),
    filters: Map().withMutations((map) => {
      filtersMap.mapKeys((key) => {
        map.set(key, Map({
          items: OrderedSet(),
          selectedItems: OrderedSet(),
          loading: false,
          searchValue: '',
        }));
      });
    }),
  });

  const reducer = (state = initialState, { type, payload }) => {
    switch (type) {
      case types.CLEAR_SELECTED_FILTERS:
        return state.update('filters', (filtersValue) => (
          filtersValue.map((filter) => filter.set('selectedItems', OrderedSet()))
        )).set('selectedDateFilter', Map({
          type: 'relative',
          value: 7 * 24 * 60 * 60,
        }));
      case types.SELECT_FILTER_ITEMS:
        return state.setIn(['filters', payload.name, 'selectedItems'], payload.items);
      case types.SELECT_DATE_FILTER:
        return state.set('selectedDateFilter', payload);
      case types.REMOVE_FILTER_ITEM:
        return state.updateIn(['filters', payload.name, 'selectedItems'], (items) => (
          items.remove(payload.value)
        ));
      case types.SET_FILTERS_FROM_URL:
        return state.withMutations((newState) => {
          newState.set('selectedDateFilter', newState.get('selectedDateFilter').mergeDeep(payload.get('selectedDateFilter')));
          newState.set('filters', newState.get('filters').mergeDeep(payload.get('filters')));
        });
      case types.FETCH_FILTERS.SUCCESS: {
        const filtersPartialState = mapFiltersToState(payload);
        return state.withMutations((newState) => {
          filtersPartialState.forEach((value, key) => {
            if (newState.hasIn(['filters', key])) {
              newState.setIn(['filters', key, 'items'], value.get('items'));
              newState.setIn(['filters', key, 'selectedItems'], updateSelectedItems(newState, key));
            }
          });
        });
      }
      case types.FETCH_USERS.SUCCESS: {
        return state.withMutations((newState) => {
          const items = newState.getIn(['filters', 'owner', 'items']);
          const users = parseUsers(payload.results, payload.isIndividualUserAllowed);
          const newItems = OrderedSet(items).merge(users);

          newState
            .setIn(['filters', 'owner', 'items'], deduplicateItems(newItems));
        });
      }
      case types.FETCH_TEAMS.SUCCESS: {
        return state.withMutations((newState) => {
          const items = newState.getIn(['filters', 'owner', 'items']);
          const teams = parseTeams(payload.results);
          const newItems = OrderedSet(items).merge(teams);
          newState
            .setIn(['filters', 'owner', 'items'], deduplicateItems(newItems));
        });
      }
      case types.INITIALIZE:
        return state.withMutations((newState) => {
          const newItems = OrderedSet(payload).concat(
            newState.getIn(['filters', 'owner', 'items']));
          newState
            .setIn(['filters', 'owner', 'items'], deduplicateItems(newItems));
          // Conditionally initialize a device groups filter, only if it's a part of the current
          // filter map (RDC & VDC have separate filters)
          if (filtersMap.has(FILTER_KEYS.DEVICE_GROUP)) {
            newState
              .setIn(['filters', FILTER_KEYS.DEVICE_GROUP, 'items'], DEVICE_GROUP_ITEMS);
          }
        });
      case types.SEARCH_METADATA.REQUEST:
        return state.withMutations((newState) => {
          const { value, filterKey } = payload;
          newState
            .setIn(['filters', filterKey, 'loading'], true)
            .setIn(['filters', filterKey, 'searchValue'], value);
        });
      case types.SEARCH_METADATA.SUCCESS:
        return state.withMutations((newState) => {
          const { data = {}, filterKey } = payload;
          const fetchedValues = data[filterKey] || [];
          const newItems = fetchedValues.map((item) => item.name).filter((item) => !!item);
          newState
            .updateIn(['filters', filterKey, 'items'], (existingItems) => existingItems.union(newItems))
            .setIn(['filters', filterKey, 'loading'], false);
        });
      case types.SEARCH_METADATA.FAILURE:
        return state.setIn(['filters', payload, 'loading'], false);
      case types.CLEANUP:
        return initialState;
      default:
        return state;
    }
  };

  return { initialState, reducer };
};
